import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import 'clsx';
import * as React from 'react';
import { useBinding } from '../../hooks/useBinding/useBinding.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';

const noop = () => {
  // noop
};
const CheckboxContext = React.createContext({
  isChecked: false,
  check: noop,
  uncheck: noop,
  toggle: noop,
  isFocused: false,
  isDisabled: false
});
const useCheckbox = () => {
  const context = React.useContext(CheckboxContext);
  if (context === undefined) {
    throw new Error('useCheckbox must be used within a CheckboxContext');
  }
  return context;
};
const Checkbox = React.forwardRef((_a, ref) => {
  var {
      isChecked: controlledChecked,
      defaultChecked,
      isDisabled = false,
      onChange,
      onFocus,
      onBlur,
      children
    } = _a,
    props = __rest(_a, ["isChecked", "defaultChecked", "isDisabled", "onChange", "onFocus", "onBlur", "children"]);
  const [isChecked, setChecked] = useBinding(defaultChecked, controlledChecked, onChange, false);
  const [isFocused, setFocused] = React.useState(false);
  const context = React.useMemo(() => ({
    isChecked,
    check: isDisabled ? noop : () => setChecked(true),
    uncheck: isDisabled ? noop : () => setChecked(false),
    toggle: isDisabled ? noop : () => setChecked(isChecked => !isChecked),
    isFocused,
    isDisabled
  }), [isChecked, isFocused, isDisabled, setChecked]);
  React.useEffect(() => {
    if (isDisabled) {
      setFocused(false);
    }
  }, [isDisabled]);
  return jsxs(CheckboxContext.Provider, {
    value: context,
    children: [jsx(VisuallyHidden, {
      children: jsx("input", Object.assign({
        ref: ref,
        checked: isChecked,
        "data-test-id": "hiddenInput",
        disabled: isDisabled,
        type: "checkbox",
        onBlur: e => {
          setFocused(false);
          onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        },
        onChange: () => {
          setChecked(isChecked => !isChecked);
        },
        onFocus: e => {
          setFocused(true);
          onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        }
      }, props))
    }), children]
  });
});
/* istanbul ignore next */
if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
  Checkbox.displayName = 'Checkbox';
}

export { Checkbox, useCheckbox };
//# sourceMappingURL=AccessibleCheckbox.js.map
